import React, { useEffect, useState } from 'react';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api, formatApiError } from '@/App';
import { toast } from 'sonner';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';
import { Receipt, Download, Eye, ArrowsClockwise } from '@phosphor-icons/react';

const money = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const AGING = [
  ['Current', 'current'],
  ['1–30 days', 'd30'],
  ['31–60 days', 'd60'],
  ['61–90 days', 'd90'],
  ['90+ days', 'd90plus'],
];

const PortalStatement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/statements/me');
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PortalLayout>
    );
  }

  if (!data) {
    return (
      <PortalLayout>
        <Card><CardContent className="p-10 text-center text-gray-500" data-testid="statement-empty">
          No statement is available for your account yet.
        </CardContent></Card>
      </PortalLayout>
    );
  }

  const pdfUrl = `/api/statements/${data.company_id}/pdf`;

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto space-y-8 fade-in" data-testid="portal-statement">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F]">Statement of Account</h1>
            <p className="text-sm text-gray-500 mt-1">{data.company_name} · as at {data.as_at}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setPreview(true)} data-testid="portal-view-statement-btn">
              <Eye className="w-4 h-4 mr-2" /> View PDF
            </Button>
            <a href={`${pdfUrl}?download=true`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="portal-download-statement-btn">
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[['Total invoiced', data.totals.invoiced, '#3B82F6'],
            ['Total paid', data.totals.paid, '#4CAF50'],
            ['Balance due', data.totals.outstanding, '#EF4444']].map(([label, value, colour]) => (
            <Card key={label} data-testid={`statement-total-${label.split(' ')[1]}`}>
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
                <p className="text-xl font-bold mt-1" style={{ color: colour }}>{money(value)}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
              <ArrowsClockwise className="w-5 h-5 text-[#F59E0B]" /> Aging of outstanding balance
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 grid grid-cols-5 gap-3" data-testid="statement-aging">
            {AGING.map(([label, key]) => (
              <div key={key} className="border border-gray-200 p-3 text-center">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-semibold text-[#0A192F] mt-1">{money(data.aging[key])}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {data.companies.map((section) => (
          <Card key={section.company_id} data-testid={`statement-section-${section.company_id}`}>
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#3B82F6]" />
                {section.company_name}
                {!section.is_root && <Badge variant="outline" className="rounded-none text-xs">Sub-account</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 overflow-x-auto">
              {section.rows.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No invoices on this account yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Reference</th>
                      <th className="py-2 pr-3">Description</th>
                      <th className="py-2 pr-3 text-right">Charge</th>
                      <th className="py-2 pr-3 text-right">Paid</th>
                      <th className="py-2 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((r, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="py-2 pr-3 text-gray-600">{r.date}</td>
                        <td className="py-2 pr-3 text-gray-600">{r.reference}</td>
                        <td className="py-2 pr-3">{r.description}</td>
                        <td className="py-2 pr-3 text-right">{r.debit ? money(r.debit) : ''}</td>
                        <td className="py-2 pr-3 text-right text-[#4CAF50]">{r.credit ? money(r.credit) : ''}</td>
                        <td className="py-2 text-right font-medium">{money(r.balance)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={5} className="py-3 text-right font-semibold">Account balance</td>
                      <td className="py-3 text-right font-bold text-[#0A192F]">{money(section.totals.outstanding)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        ))}

        <PdfPreviewDialog
          open={preview}
          onOpenChange={(open) => !open && setPreview(false)}
          url={preview ? pdfUrl : null}
          title={`Statement — ${data.company_name}`}
          filename={`Statement-${data.as_at}.pdf`}
        />
      </div>
    </PortalLayout>
  );
};

export default PortalStatement;
